import { useCallback, useMemo } from "react";
import {
  useFloatingIcon,
  useLeftBottomFloatingIcon,
  useActivities,
} from "../../../modules/homeDisplay/hooks";
import { ActivityType } from "../../../modules/homeDisplay/types/homeDisplay.types";
import { useNavigation } from "@/utils/navigation";
import { triggerPopup } from "@/utils/popupTriggers";
import FirstRechargeImg from "@/assets/homePage/FirstRecharge.webp";
import RechargeOfferImg from "@/assets/homePage/RechargeOffer.webp";
import RedPacketRainImg from "@/assets/homePage/RedPacketRain.webp";
import CashBackImg from "@/assets/homePage/cashBack.webp";
import DoubleBetIconImg from "@/assets/bet/doubleBetIcon.webp";
import RechargePromotionImg from "@/assets/homePage/newRecharge.webp";
import GridLotteryIconImg from "@/assets/gridLottery/lottery-icon.webp";
import { useFirstRechargePopup } from "./useFirstRechargePopup";
import { useCashbackPopup } from "./useCashbackPopup";
import { useRechargeOfferPopup } from "./useRechargeOfferPopup";
import { useRedPacketPopup } from "./useRedPacketPopup";
import { useSevenDayRechargePopup } from "./useSevenDayRechargePopup";
import { useBetActivityPopup } from "./useBetActivityPopup";
import { useRechargePromotionPopup } from "./useRechargePromotionPopup";
import { useGridLotteryPopup } from "./useGridLotteryPopup";
import { useRescueFundPopup } from "./useRescueFundPopup";
import { useRechargeBetPopup } from "./useRechargeBetPopup";
import { useCountdownEndListener } from "./useCountdownEndListener";
import { useFirstRechargeStagedClaimPopup } from "./useFirstRechargeStagedClaimPopup";
import {
  useWheelEntry,
  useWheelEntryClick,
} from "../../../modules/wheel/hooks";
import type { FloatingIconConfig } from "./useFloatingIcons.types";
import { utils } from "../../..";
import { POPUP_CONFIGS } from "@/config/popupConfig";

/**
 * 产品决定把 VIP 反水入口从左下角浮标迁到右下角活动栏首位。
 * admin 那条配置继续作为图标来源 + 启用开关；前端在此把它从左下角隐藏，
 * 再以"Betting Rebates"为名注入到右下角列表头部。
 *
 * 产品要求全语言固定显示 "Betting Rebates"，不走 i18n。
 * 此处是 CLAUDE.md i18n 规约的合规例外，禁止误改。
 */
const VIP_REBATE_REDIRECT = "/vip?tabIndex=1";
const VIP_REBATE_FIXED_NAME = "Betting Rebates";

function isVipRebateBanner(banner: unknown): boolean {
  if (!banner || typeof banner !== "object") return false;
  const b = banner as { redirectUrl?: unknown; internalRoute?: unknown };
  return (
    b.redirectUrl === VIP_REBATE_REDIRECT ||
    b.internalRoute === VIP_REBATE_REDIRECT
  );
}

/**
 * 产品决定：左下角浮标里的「app 下载」CUSTOM 入口，要同步到右下角活动列表顶部，
 * 让用户从活动列表也能直达下载弹窗。来源仍以左下角配置为准（imageUrl / 启用状态），
 * 前端在此把它额外注入到右下角列表头部；左下角原入口保持显示（与产品口径一致）。
 *
 * 产品要求标题固定显示 "Download App"（与 VIP 反水入口一样不走 i18n）。
 * 此处是 CLAUDE.md i18n 规约的合规例外，禁止误改。
 */
const APP_DOWNLOAD_REDIRECT = `#${POPUP_CONFIGS.APP_DOWNLOAD_DIALOG.id}`;
const APP_DOWNLOAD_FIXED_NAME = "Download App";

function isAppDownloadBanner(banner: unknown): boolean {
  if (!banner || typeof banner !== "object") return false;
  const b = banner as { redirectUrl?: unknown; internalRoute?: unknown };
  return (
    b.redirectUrl === APP_DOWNLOAD_REDIRECT ||
    b.internalRoute === APP_DOWNLOAD_REDIRECT
  );
}

/**
 * 统一管理所有浮窗图标的显示逻辑
 */
export const useFloatingIcons = () => {
  // 获取后台配置的浮窗列表
  const floatingIconBanners = useFloatingIcon();
  const leftBottomBanners = useLeftBottomFloatingIcon();
  const activities = useActivities();
  // 获取各个业务逻辑的 hooks
  const firstRecharge = useFirstRechargePopup();
  const cashback = useCashbackPopup();
  const rechargeOffer = useRechargeOfferPopup();
  const redPacket = useRedPacketPopup();
  const sevenDayRecharge = useSevenDayRechargePopup();
  const betActivity = useBetActivityPopup();
  const rechargePromotion = useRechargePromotionPopup();
  const gridLottery = useGridLotteryPopup();
  const rescueFund = useRescueFundPopup();
  const firstRechargeStaged = useFirstRechargeStagedClaimPopup();
  const rechargeBet = useRechargeBetPopup();
  // 抽奖转盘：配置来自 /wheel/config（Wheel 实体），不走 homeDisplay displayItems
  const { wheel, showFloatIcon, floatIconImage } = useWheelEntry();
  const wheelEntryClick = useWheelEntryClick();
  const { navigate } = useNavigation();

  // 为所有带倒计时的浮窗添加倒计时结束监听
  useCountdownEndListener(firstRecharge.remainingMs);
  useCountdownEndListener(cashback.remainingMs);
  useCountdownEndListener(rechargeOffer.remainingMs);
  useCountdownEndListener(redPacket.remainingMs);
  useCountdownEndListener(sevenDayRecharge.remainingMs);
  useCountdownEndListener(betActivity.remainingMs);
  useCountdownEndListener(gridLottery.remainingMs);
  useCountdownEndListener(firstRechargeStaged.remainingMs);
  // useCountdownEndListener(rescueFund.remainingMs); // 救援金不显示倒计时

  // 为充值活动的所有 banner 添加倒计时监听（传入数组）
  const rechargePromotionRemainingMsList = useMemo(
    () =>
      rechargePromotion.banners
        .map(banner => rechargePromotion.getBannerRemainingMs(banner))
        .filter(ms => Number.isFinite(ms) && ms > 0),
    [rechargePromotion.banners, rechargePromotion.getBannerRemainingMs]
  );
  useCountdownEndListener(rechargePromotionRemainingMsList);

  // 单个 banner → FloatingIconConfig：右下角与左下角（活动类）共用同一份业务逻辑
  const buildActivityIconConfig = (banner: any): FloatingIconConfig | null => {
    const activityType = (banner as any)?.activityType as
      | ActivityType
      | undefined;
    const imageUrl = (banner as any)?.imageUrl || "";
    const sort = (banner as any)?.sort || 0;
    const activityId = (banner as any)?.activityId || "";
    const activityName = (banner as any)?.activityName as string | undefined;

    let config: FloatingIconConfig | null = null;
    switch (activityType) {
      case ActivityType.RESCUE_FUND: {
        // 与 useRescueFundPopup 的 claimableByActivityId 对齐：支持 activityId 或 id（activity.id / taskGroupId）
        const rescueFundActivityId =
          (banner as any)?.activityId ?? (banner as any)?.id ?? "";
        if (
          rescueFund.shouldShow &&
          rescueFund.isActivityAvailable?.(rescueFundActivityId)
        ) {
          config = {
            id: `rescue-fund-${banner.id}`,
            activityType,
            sort,
            imageUrl: utils.getImageUrl(imageUrl),
            shouldShow: true,
            onClick: () => {
              rescueFund.trigger(rescueFundActivityId);
            },
            badge: {
              show: rescueFund.getShouldShowBadge(rescueFundActivityId),
              type: "exclamation",
              count: rescueFund.getBadgeCount?.(rescueFundActivityId), // 今天未打开过→undefined 显示感叹号，今天已打开过→1 显示数字 1
            },
            // 隐藏倒计时
            // countdown: {
            //   remainingMs: rescueFund.remainingMs,
            // },
          };
        }
        break;
      }
      case ActivityType.FIRST_DEPOSIT:
      case ActivityType.FIRST_RECHARGE_STAGED: {
        /** 与后台仅配置一条「首充」浮标：STAGED_MANUAL + showStagedUi 时同图标走分批领取，否则走旧首充营销。
         * 后台可能用 FIRST_DEPOSIT 或 FIRST_RECHARGE_STAGED，需同时匹配。 */
        if (firstRechargeStaged.shouldShowFloating) {
          const n = firstRechargeStaged.claimableCount;
          config = {
            id: `first-deposit-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? FirstRechargeImg
                : utils.getImageUrl(imageUrl) || FirstRechargeImg,
            shouldShow: true,
            onClick: firstRechargeStaged.trigger,
            countdown: {
              remainingMs: firstRechargeStaged.remainingMs,
            },
            badge:
              n > 0
                ? {
                    show: true,
                    type: "exclamation" as const,
                    count: n,
                  }
                : undefined,
          };
        } else if (firstRecharge.shouldShow) {
          config = {
            id: `first-deposit-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? FirstRechargeImg
                : utils.getImageUrl(imageUrl) || FirstRechargeImg,
            shouldShow: true,
            onClick: firstRecharge.trigger,
            countdown: {
              remainingMs: firstRecharge.remainingMs,
            },
          };
        }
        break;
      }

      case ActivityType.FIVE_DAILY_RECHARGE:
        // Cashback (5冲)
        if (cashback.shouldShow) {
          config = {
            id: `cashback-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? CashBackImg
                : utils.getImageUrl(imageUrl) || CashBackImg,
            shouldShow: true,
            onClick: cashback.trigger,
            countdown: {
              remainingMs: cashback.remainingMs,
            },
          };
        }
        break;

      case ActivityType.RECHARGE_PROMOTION: {
        // 充值任务 - 通过 activityId 匹配，使用 getTriggerByTaskId 获取触发器
        const rechargeTaskTrigger =
          rechargeOffer.getTriggerByTaskId(activityId);

        if (rechargeTaskTrigger) {
          const matchedTask = rechargeOffer.rechargeTasks.find(
            t => t.task.taskId === activityId
          );

          if (matchedTask && matchedTask.task.rewardAmount > 0) {
            config = {
              id: `recharge-task-${banner.id}`,
              activityType,
              sort,
              imageUrl:
                utils.getImageUrl(imageUrl) === "/not-found.jpg"
                  ? RechargeOfferImg
                  : utils.getImageUrl(imageUrl) || RechargeOfferImg,
              shouldShow: true,
              onClick: rechargeTaskTrigger,
              countdown: {
                remainingMs: rechargeOffer.remainingMs,
              },
            };
          }
        }
        break;
      }

      case ActivityType.GROUPED_RECHARGE_TASK: {
        // 分组充值任务 - 所有 activityId 都映射到合并弹窗 grouped-all
        const groupedTrigger = rechargeOffer.getTriggerByTaskId(activityId);

        if (rechargeOffer.shouldShow && groupedTrigger) {
          const countdownMs = rechargeOffer.groupedRechargeRemainingMs;
          const shouldShowBadge = rechargeOffer.shouldShowGroupedBadge;
          config = {
            id: `grouped-recharge-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? RechargeOfferImg
                : utils.getImageUrl(imageUrl) || RechargeOfferImg,
            shouldShow: true,
            onClick: groupedTrigger,
            badge: {
              show: shouldShowBadge,
              type: "exclamation",
            },
            countdown:
              Number.isFinite(countdownMs) && countdownMs > 0
                ? { remainingMs: countdownMs }
                : undefined,
          };
        }
        break;
      }

      case ActivityType.RED_PACKET_RAIN:
        if (redPacket.shouldShow) {
          config = {
            id: `red-packet-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? RedPacketRainImg
                : utils.getImageUrl(imageUrl) || RedPacketRainImg,
            shouldShow: true,
            onClick: redPacket.trigger,
            badge: {
              show: redPacket.isOpen,
              type: "dot",
            },
            countdown: {
              remainingMs: redPacket.remainingMs,
            },
          };
        }
        break;
      case ActivityType.DAILY_RECHARGE:
        if (sevenDayRecharge.shouldShow) {
          config = {
            id: `seven-day-recharge-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? CashBackImg
                : utils.getImageUrl(imageUrl) || CashBackImg,
            shouldShow: true,
            onClick: sevenDayRecharge.trigger,
            badge: {
              show: sevenDayRecharge.shouldShowBadge,
              type: "exclamation",
            },
            countdown: {
              remainingMs: sevenDayRecharge.remainingMs,
            },
          };
        }
        break;
      case ActivityType.BET_TASK:
        if (betActivity.shouldShow) {
          const claimableCount = betActivity.day0ClaimableCount || 0;

          // 获取 displayConfig
          const betTask = betActivity.betTask;
          const displayConfig = betTask?.displayConfig;

          // 根据是否为 double bet 选择不同的图片
          let finalImageUrl = DoubleBetIconImg;
          if (
            betActivity.isDoubleBetActive &&
            displayConfig?.betDoubleFloatingIconImageUrl
          ) {
            finalImageUrl = utils.getImageUrl(
              displayConfig.betDoubleFloatingIconImageUrl
            );
          } else if (
            imageUrl &&
            utils.getImageUrl(imageUrl) !== "/not-found.jpg"
          ) {
            finalImageUrl = utils.getImageUrl(imageUrl);
          }

          config = {
            id: `bet-activity-${banner.id}`,
            activityType,
            sort,
            imageUrl: finalImageUrl,
            shouldShow: true,
            onClick: betActivity.handleBetActivityClick,
            badge: {
              show: betActivity.shouldShowBadge || claimableCount > 0,
              type: "exclamation",
              count: claimableCount > 0 ? claimableCount : undefined,
            },
            countdown: betActivity.showCountdown
              ? {
                  remainingMs: betActivity.remainingMs,
                  isDoubleBet: betActivity.isDoubleBetActive,
                  doubleMultiplier: betActivity.doubleMultiplier,
                }
              : undefined,
          };
        }
        break;

      case ActivityType.RECHARGE_ACTIVITY: {
        // 充值活动 - 通过 activityId 匹配，使用 getTriggerByActivityId 获取触发器
        const trigger = rechargePromotion.getTriggerByActivityId(activityId);

        if (trigger) {
          const matchedBanner = rechargePromotion.banners.find(
            b => (b as any)?.activityGroupId === activityId
          );

          if (matchedBanner && !rechargePromotion.isAllClaimed(matchedBanner)) {
            const hasClaimable =
              rechargePromotion.hasClaimableUnclaimed(matchedBanner);
            const shouldHideTime =
              rechargePromotion.shouldHideBannerTime(matchedBanner);

            config = {
              id: `recharge-activity-${banner.id}`,
              activityType,
              sort,
              imageUrl:
                utils.getImageUrl(imageUrl) === "/not-found.jpg"
                  ? RechargePromotionImg
                  : utils.getImageUrl(imageUrl) || RechargePromotionImg,
              shouldShow: true,
              onClick: trigger,
              badge: {
                show: hasClaimable,
                type: "exclamation",
              },
              countdown: shouldHideTime
                ? undefined
                : {
                    remainingMs:
                      rechargePromotion.getBannerRemainingMs(matchedBanner),
                  },
            };
          }
        }
        break;
      }

      case ActivityType.RECHARGE_BET: {
        // 充值+投注活动 - 通过 activityId 匹配触发弹窗
        // shouldShow 已在 useRechargeBetPopup 里统一控制过期逻辑，与 RESCUE_FUND 保持一致先检查
        const rechargeBetTrigger =
          rechargeBet.shouldShow &&
          rechargeBet.getTriggerByActivityId(activityId);
        if (rechargeBetTrigger) {
          const remainingMs =
            rechargeBet.getRemainingMsByActivityId(activityId);
          const claimableCount =
            rechargeBet.getClaimableCountByActivityId(activityId);
          const shouldShowBadge =
            rechargeBet.getShouldShowBadgeByActivityId(activityId);
          config = {
            id: `recharge-bet-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? RechargePromotionImg
                : utils.getImageUrl(imageUrl) || RechargePromotionImg,
            shouldShow: true,
            onClick: rechargeBetTrigger,
            badge: {
              show: shouldShowBadge,
              type: "exclamation",
              count: claimableCount > 0 ? claimableCount : undefined,
            },
            countdown:
              Number.isFinite(remainingMs) && remainingMs > 0
                ? { remainingMs }
                : undefined,
          };
        }
        break;
      }

      case ActivityType.NINE_GRID_ACTIVITY: {
        // 九宫格活动
        if (gridLottery.shouldShow) {
          config = {
            id: `grid-lottery-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? GridLotteryIconImg
                : utils.getImageUrl(imageUrl) || GridLotteryIconImg,
            shouldShow: true,
            onClick: gridLottery.trigger,
            badge: {
              show: gridLottery.shouldShowBadge,
              type: "exclamation",
              count: gridLottery.claimableCount > 0 ? 1 : undefined,
            },
            countdown:
              gridLottery.remainingMs > 0
                ? {
                    remainingMs: gridLottery.remainingMs,
                  }
                : undefined,
          };
        }
        break;
      }

      default:
        // 如果没有 activityType，可能是自定义的浮窗（左下角 CUSTOM 跳转）
        break;
    }

    if (config) {
      if (activityName) {
        config.activityName = activityName;
      }
      // 按活动类型取各自的 landingPageTitle
      const landingPageTitle = (() => {
        switch (activityType) {
          case ActivityType.DAILY_RECHARGE:
            return sevenDayRecharge.landingPageTitle;
          case ActivityType.NINE_GRID_ACTIVITY:
            return gridLottery.landingPageTitle;
          case ActivityType.BET_TASK:
            return betActivity.betTask?.displayConfig?.landingPageTitle;
          case ActivityType.RESCUE_FUND: {
            const list = activities?.RESCUE_FUND?.list || [];
            const match = list.find(
              (a: any) => a?.id === activityId || a?.taskGroupId === activityId
            );
            return (match as any)?.displayConfig?.ruleTitle;
          }
          case ActivityType.RECHARGE_ACTIVITY: {
            const list = activities?.RECHARGE_ACTIVITY?.list || [];
            const match = list.find(
              (a: any) =>
                a?.activityGroupId === activityId || a?.id === activityId
            );
            return match?.activityEntryPoint?.landingPageTitle;
          }
          default:
            return undefined;
        }
      })();
      if (landingPageTitle) {
        config.landingPageTitle = landingPageTitle;
      }
    }
    return config;
  };

  // 右下角浮窗图标：仅活动类
  const floatingIcons = useMemo(() => {
    const icons: FloatingIconConfig[] = [];
    floatingIconBanners.forEach(banner => {
      const config = buildActivityIconConfig(banner);
      if (config) icons.push(config);
    });

    // 抽奖转盘浮动图标（admin 侧强制开启）：启用即追加到列表尾部
    if (showFloatIcon && wheel) {
      const wheelIconUrl = utils.getImageUrl(floatIconImage || "");
      if (wheelIconUrl && wheelIconUrl !== "/not-found.jpg") {
        icons.push({
          id: `lucky-wheel-${wheel.id}`,
          activityType: ActivityType.LUCKY_WHEEL_ACTIVITY,
          activityName: wheel.name,
          landingPageTitle: wheel.displayConfig?.ruleTitle,
          imageUrl: wheelIconUrl,
          shouldShow: true,
          onClick: wheelEntryClick,
          sort: Number.MAX_SAFE_INTEGER,
        });
      }
    }

    // 按 sort 升序，-Infinity 稳定居首
    return icons.sort((a, b) => a.sort - b.sort);
  }, [
    floatingIconBanners,
    leftBottomBanners,
    wheel,
    showFloatIcon,
    floatIconImage,
    wheelEntryClick,
    firstRecharge,
    firstRechargeStaged,
    cashback,
    rechargeOffer,
    redPacket,
    sevenDayRecharge,
    betActivity,
    rechargePromotion,
    gridLottery,
    rescueFund,
    rechargeBet,
    activities,
    navigate,
  ]);

  // 左下角浮窗图标：CUSTOM 跳转（如客服）+ 活动类，与右下角共用同一份业务 hooks
  // 后台配置什么就显示什么，不再跳过 VIP 反水入口和 app 下载入口
  const leftBottomFloatingIcons = useMemo(() => {
    // 调试日志：原始数据
    console.log("[useFloatingIcons] leftBottomBanners 原始数据:", {
      count: leftBottomBanners.length,
      banners: leftBottomBanners.map((b: any) => ({
        id: b?.id,
        name: b?.name,
        activityType: b?.activityType,
        sourceType: b?.sourceType,
        redirectUrl: b?.redirectUrl,
        internalRoute: b?.internalRoute,
        imageUrl: b?.imageUrl,
        isVipRebate: isVipRebateBanner(b),
        isAppDownload: isAppDownloadBanner(b),
      })),
    });

    const icons: FloatingIconConfig[] = [];
    leftBottomBanners.forEach(banner => {
      const activityType = (banner as any)?.activityType;
      const sourceType = (banner as any)?.sourceType;

      console.log("[useFloatingIcons] 处理 banner:", {
        id: (banner as any)?.id,
        activityType,
        sourceType,
        redirectUrl: (banner as any)?.redirectUrl,
      });

      if (activityType || sourceType === "ACTIVITY") {
        const config = buildActivityIconConfig(banner);
        console.log(
          "[useFloatingIcons] 活动类 banner 结果:",
          config ? "有配置" : "无配置",
          config
        );
        if (config) icons.push(config);
        return;
      }
      // CUSTOM 类：通过 redirectUrl 跳转，不依赖活动业务 hook
      const rawImageUrl = (banner as any)?.imageUrl || "";
      const imageUrl = utils.getImageUrl(rawImageUrl);
      console.log("[useFloatingIcons] CUSTOM 类 banner:", {
        rawImageUrl,
        imageUrl,
        isValid: imageUrl && imageUrl !== "/not-found.jpg",
      });
      if (!imageUrl || imageUrl === "/not-found.jpg") return;
      const redirectUrl =
        (banner as any)?.redirectUrl || (banner as any)?.internalRoute || "";
      const sort = (banner as any)?.sort || 0;
      const id = (banner as any)?.id || "";
      icons.push({
        id: `left-bottom-custom-${id}`,
        sort,
        imageUrl,
        shouldShow: true,
        redirectUrl,
        onClick: () => {},
      });
    });

    console.log("[useFloatingIcons] leftBottomFloatingIcons 最终结果:", {
      count: icons.length,
      icons: icons.map(i => ({
        id: i.id,
        imageUrl: i.imageUrl,
        redirectUrl: i.redirectUrl,
      })),
    });

    return icons.sort((a, b) => a.sort - b.sort);
  }, [
    leftBottomBanners,
    firstRecharge,
    firstRechargeStaged,
    cashback,
    rechargeOffer,
    redPacket,
    sevenDayRecharge,
    betActivity,
    rechargePromotion,
    gridLottery,
    rescueFund,
    rechargeBet,
    activities,
  ]);

  const PopupRegistrationComponent = useCallback(() => {
    const RechargePromotionPopups =
      rechargePromotion.PopupRegistrationComponent;
    const RechargeOfferPopups = rechargeOffer.PopupRegistrationComponent;
    const RechargeBetPopups = rechargeBet.PopupRegistrationComponent;
    return (
      <>
        <RechargePromotionPopups />
        <RechargeOfferPopups />
        <RechargeBetPopups />
      </>
    );
  }, [
    rechargePromotion.PopupRegistrationComponent,
    rechargeOffer.PopupRegistrationComponent,
    rechargeBet.PopupRegistrationComponent,
  ]);

  return {
    floatingIcons,
    leftBottomFloatingIcons,
    firstRecharge,
    cashback,
    rechargeOffer,
    redPacket,
    sevenDayRecharge,
    betActivity,
    rechargePromotion,
    gridLottery,
    PopupRegistrationComponent,
  };
};
